import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  startDownloadSearchFieldConfigsExcel,
  startImportSearchFieldConfigsExcel,
  startLoadSearchFieldConfigs,
  startUpdateSearchFieldConfig,
} from '../../app/actions/semanticIntegrationActions';
import {
  isIntegrationFilterBarEnabled,
  isM4FromTableEnabled,
  isPanelComboFromTableEnabled,
  isPanelLexicalFromTableEnabled,
} from '../../app/helpers/semanticSearchConfigHelper';
import SearchFilterFieldCard from './SearchFilterFieldCard';
import {
  SURFACE_IDS,
  buildSurfacePatch,
  isCandidate,
  isOnSurface,
  otherSurfaceIds,
  toggleOffBlockedReason,
} from './searchFilterSurfaceRegistry';
import './adminSemanticSearchFilters.css';

const MODULE_KEYS = [
  'technology',
  'alternative',
  'experience',
  'innovation',
  'assistance',
  'financial',
  'vision',
  'regulation',
];

const BOOL_FLAGS = [
  'use_in_like',
  'use_in_combo',
  'use_in_panel',
  'use_in_bridge',
  'use_in_m4',
  'activated',
  'deleted',
];

const WIDGET_OPTIONS = ['none', 'select_id', 'ciiu_level', 'problem'];

const isTruthy = (value) => value === true || value === 'true' || value === 1 || value === '1';

const AdminSemanticSearchFiltersScreen = () => {
  const { t } = useTranslation();
  const configuration = useSelector((state) => state.configuration);
  const [moduleKey, setModuleKey] = useState('technology');
  const [surfaceId, setSurfaceId] = useState('panel_l3');
  const [viewMode, setViewMode] = useState('form');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [onlyCombos, setOnlyCombos] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const fileInputRef = useRef(null);

  const barOn = isIntegrationFilterBarEnabled(configuration);
  const panelFromTable = isPanelLexicalFromTableEnabled(configuration);
  const panelComboFromTable = isPanelComboFromTableEnabled(configuration);
  const m4FromTable = isM4FromTableEnabled(configuration);

  const load = async (nextModule = moduleKey, nextIncludeDeleted = includeDeleted) => {
    setLoading(true);
    const resp = await startLoadSearchFieldConfigs(nextModule, nextIncludeDeleted)();
    setLoading(false);
    if (resp?.ok) setRows(resp.rows || []);
  };

  useEffect(() => {
    load(moduleKey, includeDeleted);
    setExpandedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey, includeDeleted]);

  const searchedRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (!needle) return true;
      const label = t(`semanticSearch.filterFields.${row.field_name}`, { defaultValue: row.field_name });
      return `${row.field_name} ${label} ${row.relation_as || ''}`.toLowerCase().includes(needle);
    });
  }, [rows, query, t]);

  const surfaceRows = useMemo(() => {
    const list = searchedRows.filter((row) => isOnSurface(row, surfaceId));
    return [...list].sort((a, b) => (a.priority || 0) - (b.priority || 0) || a.id - b.id);
  }, [searchedRows, surfaceId]);

  const candidateRows = useMemo(() => {
    if (surfaceId === 'all') return [];
    return searchedRows
      .filter((row) => isCandidate(row, surfaceId))
      .sort((a, b) => String(a.field_name).localeCompare(String(b.field_name)));
  }, [searchedRows, surfaceId]);

  const visibleRows = useMemo(() => {
    const list = onlyCombos
      ? searchedRows.filter((row) => isTruthy(row.use_in_combo) || (row.combo_widget && row.combo_widget !== 'none'))
      : searchedRows;
    return [...list].sort((a, b) => {
      const aCombo = isTruthy(a.use_in_combo) ? 0 : 1;
      const bCombo = isTruthy(b.use_in_combo) ? 0 : 1;
      if (aCombo !== bCombo) return aCombo - bCombo;
      return (a.priority || 0) - (b.priority || 0);
    });
  }, [searchedRows, onlyCombos]);

  const patchRow = async (row, patch) => {
    const clean = { ...patch };
    Object.keys(clean).forEach((key) => {
      if (clean[key] === undefined) delete clean[key];
    });
    if (!Object.keys(clean).length) return;
    setSavingId(row.id);
    const prev = rows;
    setRows((current) => current.map((item) => (item.id === row.id ? { ...item, ...clean } : item)));
    const resp = await startUpdateSearchFieldConfig(row.id, clean)();
    setSavingId(null);
    if (!resp?.ok) {
      setRows(prev);
      Swal.fire('Error', resp?.msg || t('semanticSearch.adminFilters.saveError'), 'error');
      return;
    }
    if (resp.row) {
      setRows((current) => current.map((item) => (item.id === row.id ? { ...item, ...resp.row } : item)));
    }
  };

  const onToggle = (row, key) => {
    const next = !isTruthy(row[key]);
    const patch = { [key]: next };
    if (key === 'deleted' && next) patch.activated = false;
    if (key === 'use_in_combo' && next && (!row.combo_widget || row.combo_widget === 'none')) {
      if (row.field_name === 'catalogciiu_levels') patch.combo_widget = 'ciiu_level';
      else if (String(row.field_name || '').includes('problem')) patch.combo_widget = 'problem';
      else patch.combo_widget = 'select_id';
    }
    patchRow(row, patch);
  };

  const onToggleSurface = (row, enable) => {
    if (!enable && toggleOffBlockedReason(row, surfaceId)) {
      Swal.fire(
        t('semanticSearch.adminFilters.formLockedTitle'),
        t(`semanticSearch.adminFilters.formLocked.${toggleOffBlockedReason(row, surfaceId)}`),
        'info'
      );
      return;
    }
    const patch = buildSurfacePatch(row, surfaceId, enable);
    if (!patch) {
      Swal.fire(
        t('semanticSearch.adminFilters.formLockedTitle'),
        t('semanticSearch.adminFilters.formLocked.generic'),
        'info'
      );
      return;
    }
    patchRow(row, patch);
  };

  const onDownloadExcel = async () => {
    setExcelBusy(true);
    const resp = await startDownloadSearchFieldConfigsExcel(true)();
    setExcelBusy(false);
    if (resp?.ok) {
      Swal.fire(t('semanticSearch.adminFilters.excelOkTitle'), t('semanticSearch.adminFilters.excelDownloadOk'), 'success');
    }
  };

  const runImport = async (file, apply) => {
    setExcelBusy(true);
    const resp = await startImportSearchFieldConfigsExcel(file, apply)();
    setExcelBusy(false);
    if (!resp?.ok) {
      Swal.fire('Error', resp?.msg || t('semanticSearch.adminFilters.excelImportError'), 'error');
      return;
    }
    setImportSummary(resp);
    const invalid = resp.numberOfRowsInvalid || 0;
    if (apply) {
      await load();
      Swal.fire(
        t('semanticSearch.adminFilters.excelOkTitle'),
        t('semanticSearch.adminFilters.excelApplyOk', {
          saved: resp.numberOfRowsSaved || 0,
          updated: resp.toUpdate || 0,
          inserted: resp.toInsert || 0,
        }),
        'success'
      );
      return;
    }
    Swal.fire(
      t('semanticSearch.adminFilters.excelOkTitle'),
      t('semanticSearch.adminFilters.excelValidateOk', {
        total: resp.totalRows || 0,
        valid: resp.numberOfRowsValid || 0,
        invalid,
        updated: resp.toUpdate || 0,
        inserted: resp.toInsert || 0,
      }),
      invalid ? 'warning' : 'success'
    );
  };

  const onPickExcel = async (event, apply) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    await runImport(file, apply);
  };

  const comboPreview = surfaceId === 'bar_combo'
    ? surfaceRows
    : rows.filter((row) => isTruthy(row.activated) && !isTruthy(row.deleted) && isTruthy(row.use_in_combo) && isTruthy(row.use_in_bridge));

  const previewList = (surfaceId === 'all' ? comboPreview : surfaceRows)
    .map((row) => t(`semanticSearch.filterFields.${row.field_name}`, { defaultValue: row.field_name }))
    .join(', ');

  return (
    <div className="container-fluid mt-4 admin-search-filters">
      <h2>{t('semanticSearch.adminFilters.title')}</h2>
      <p className="text-muted">{t('semanticSearch.adminFilters.subtitle')}</p>

      <Alert variant="info">
        <strong>{t('semanticSearch.adminFilters.flowTitle')}</strong>
        <ol className="mb-0 mt-2">
          <li>{t('semanticSearch.adminFilters.flow1')}</li>
          <li>{t('semanticSearch.adminFilters.flow2')}</li>
          <li>{t('semanticSearch.adminFilters.flow3')}</li>
          <li>{t('semanticSearch.adminFilters.flow4')}</li>
          <li>{t('semanticSearch.adminFilters.flow5')}</li>
        </ol>
      </Alert>

      <Alert variant={barOn ? 'success' : 'warning'}>
        {t('semanticSearch.adminFilters.flagBar', { value: String(barOn) })}
        {' · '}
        {t('semanticSearch.adminFilters.flagPanel', { value: String(panelFromTable) })}
        {' · '}
        {t('semanticSearch.adminFilters.flagPanelCombo', { value: String(panelComboFromTable) })}
        {' · '}
        {t('semanticSearch.adminFilters.flagM4', { value: String(m4FromTable) })}
        <div className="small mt-2 mb-0">{t('semanticSearch.adminFilters.flagHint')}</div>
      </Alert>

      <div className="admin-search-filters__toolbar">
        <Form.Group>
          <Form.Label htmlFor="search-filters-module">{t('semanticSearch.adminFilters.module')}</Form.Label>
          <Form.Select
            id="search-filters-module"
            value={moduleKey}
            onChange={(event) => setModuleKey(event.target.value)}
          >
            {MODULE_KEYS.map((key) => (
              <option key={key} value={key}>{t(`semanticSearch.module.${key}`)}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="search-filters-surface">{t('semanticSearch.adminFilters.formSurface')}</Form.Label>
          <Form.Select
            id="search-filters-surface"
            value={surfaceId}
            onChange={(event) => {
              setSurfaceId(event.target.value);
              setExpandedId(null);
            }}
          >
            {SURFACE_IDS.map((id) => (
              <option key={id} value={id}>{t(`semanticSearch.adminFilters.surface.${id}`)}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="search-filters-query">{t('semanticSearch.adminFilters.formSearch')}</Form.Label>
          <Form.Control
            id="search-filters-query"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('semanticSearch.adminFilters.formSearchPh')}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label htmlFor="search-filters-view">{t('semanticSearch.adminFilters.formView')}</Form.Label>
          <Form.Select
            id="search-filters-view"
            value={viewMode}
            onChange={(event) => setViewMode(event.target.value)}
          >
            <option value="form">{t('semanticSearch.adminFilters.formViewForm')}</option>
            <option value="table">{t('semanticSearch.adminFilters.formViewTable')}</option>
          </Form.Select>
        </Form.Group>
        <Form.Check
          type="switch"
          id="search-filters-include-deleted"
          label={t('semanticSearch.adminFilters.includeDeleted')}
          checked={includeDeleted}
          onChange={(event) => setIncludeDeleted(event.target.checked)}
        />
        {viewMode === 'table' && (
          <Form.Check
            type="switch"
            id="search-filters-only-combos"
            label={t('semanticSearch.adminFilters.onlyCombos')}
            checked={onlyCombos}
            onChange={(event) => setOnlyCombos(event.target.checked)}
          />
        )}
        <Button variant="outline-secondary" size="sm" onClick={() => load()} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : t('semanticSearch.adminFilters.refresh')}
        </Button>
        <Button variant="outline-primary" size="sm" onClick={onDownloadExcel} disabled={excelBusy}>
          {excelBusy ? <Spinner animation="border" size="sm" /> : t('semanticSearch.adminFilters.excelDownload')}
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={excelBusy}
          onClick={() => {
            if (!fileInputRef.current) return;
            fileInputRef.current.dataset.apply = 'false';
            fileInputRef.current.click();
          }}
        >
          {t('semanticSearch.adminFilters.excelValidate')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={excelBusy}
          onClick={() => {
            if (!fileInputRef.current) return;
            fileInputRef.current.dataset.apply = 'true';
            fileInputRef.current.click();
          }}
        >
          {t('semanticSearch.adminFilters.excelApply')}
        </Button>
        <Form.Control
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          className="d-none"
          onChange={(event) => onPickExcel(event, fileInputRef.current?.dataset.apply === 'true')}
        />
        <Link className="btn btn-link btn-sm" to="/admin-semantic-search">
          {t('semanticSearch.adminFilters.backFlags')}
        </Link>
        <Link className="btn btn-link btn-sm" to="/admin-configuration">
          {t('semanticSearch.adminFilters.openConfigs')}
        </Link>
      </div>

      {importSummary && (
        <p className="small text-muted">
          {t('semanticSearch.adminFilters.excelLast', {
            total: importSummary.totalRows || 0,
            valid: importSummary.numberOfRowsValid || 0,
            invalid: importSummary.numberOfRowsInvalid || 0,
            applied: String(Boolean(importSummary.applied)),
          })}
        </p>
      )}

      <Alert variant="light" className="border">
        <div>{t(`semanticSearch.adminFilters.surfaceHelp.${surfaceId}`)}</div>
        <div className="small mt-2 mb-0">
          {t('semanticSearch.adminFilters.formPreview', {
            list: previewList || t('semanticSearch.adminFilters.previewEmpty'),
          })}
        </div>
      </Alert>

      {viewMode === 'form' && (
        <>
          {loading && <Spinner animation="border" size="sm" />}
          <div className="admin-search-filters__grid">
            {surfaceRows.map((row) => (
              <SearchFilterFieldCard
                key={row.id}
                row={row}
                surfaceId={surfaceId}
                busy={savingId === row.id || loading}
                expanded={expandedId === row.id}
                included={surfaceId === 'all' ? isTruthy(row.activated) && !isTruthy(row.deleted) : true}
                otherIds={otherSurfaceIds(row, surfaceId)}
                blockedReason={toggleOffBlockedReason(row, surfaceId)}
                onToggleSurface={onToggleSurface}
                onPatch={patchRow}
                onToggleExpand={(id) => setExpandedId((current) => (current === id ? null : id))}
              />
            ))}
          </div>
          {!loading && surfaceRows.length === 0 && (
            <p className="text-muted">{t('semanticSearch.adminFilters.formEmptySurface')}</p>
          )}

          {candidateRows.length > 0 && (
            <div className="mt-4">
              <h5>{t('semanticSearch.adminFilters.formCandidates')}</h5>
              <p className="small text-muted">{t('semanticSearch.adminFilters.formCandidatesHint')}</p>
              <div className="admin-search-filters__candidates">
                {candidateRows.map((row) => {
                  const label = t(`semanticSearch.filterFields.${row.field_name}`, { defaultValue: row.field_name });
                  return (
                    <Button
                      key={row.id}
                      variant="outline-secondary"
                      size="sm"
                      disabled={savingId === row.id || loading}
                      onClick={() => onToggleSurface(row, true)}
                    >
                      + {label}
                      <code className="ms-1">{row.field_name}</code>
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {viewMode === 'table' && (
        <div className="table-responsive admin-search-filters__table">
          <Table size="sm" bordered hover>
            <thead>
              <tr>
                <th>{t('semanticSearch.adminFilters.colField')}</th>
                <th>{t('semanticSearch.adminFilters.colKind')}</th>
                <th title={t('semanticSearch.adminFilters.helpLike')}>LIKE</th>
                <th title={t('semanticSearch.adminFilters.helpCombo')}>Combo</th>
                <th title={t('semanticSearch.adminFilters.helpPanel')}>Panel</th>
                <th title={t('semanticSearch.adminFilters.helpBridge')}>Barra</th>
                <th title={t('semanticSearch.adminFilters.helpM4')}>M4</th>
                <th>{t('semanticSearch.adminFilters.colWidget')}</th>
                <th title={t('semanticSearch.adminFilters.helpActivated')}>ON</th>
                <th title={t('semanticSearch.adminFilters.helpDeleted')}>Del</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                const busy = savingId === row.id;
                const dimmed = !isTruthy(row.activated) || isTruthy(row.deleted);
                const label = t(`semanticSearch.filterFields.${row.field_name}`, { defaultValue: row.field_name });
                return (
                  <tr key={row.id} className={dimmed ? 'admin-search-filters__dim' : undefined}>
                    <td>
                      <code>{row.field_name}</code>
                      <div className="small text-muted">{label}</div>
                      {row.relation_as && <div className="small text-muted">{row.relation_as}</div>}
                    </td>
                    <td>{row.source_kind || 'column'}</td>
                    {BOOL_FLAGS.filter((key) => key !== 'activated' && key !== 'deleted').map((key) => (
                      <td key={key} className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={isTruthy(row[key])}
                          disabled={busy || loading}
                          onChange={() => onToggle(row, key)}
                          aria-label={`${key} ${row.field_name}`}
                        />
                      </td>
                    ))}
                    <td>
                      <Form.Select
                        size="sm"
                        value={row.combo_widget || 'none'}
                        disabled={busy || loading}
                        onChange={(event) => patchRow(row, { combo_widget: event.target.value })}
                      >
                        {WIDGET_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={isTruthy(row.activated)}
                        disabled={busy || loading}
                        onChange={() => onToggle(row, 'activated')}
                        aria-label={`activated ${row.field_name}`}
                      />
                    </td>
                    <td className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={isTruthy(row.deleted)}
                        disabled={busy || loading}
                        onChange={() => onToggle(row, 'deleted')}
                        aria-label={`deleted ${row.field_name}`}
                      />
                    </td>
                  </tr>
                );
              })}
              {!loading && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-muted">
                    {t('semanticSearch.adminFilters.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default AdminSemanticSearchFiltersScreen;
